export const GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY = "guest_spatius_avatar_v1";

export const GUEST_SPATIUS_AVATAR_VARIANTS = {
  control: "control",
  spatiusAvatar: "spatius_avatar",
} as const;

export type GuestSpatiusAvatarVariant =
  (typeof GUEST_SPATIUS_AVATAR_VARIANTS)[keyof typeof GUEST_SPATIUS_AVATAR_VARIANTS];

export type GuestSpatiusAvatarExperimentProperties = {
  experiment_key: typeof GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY;
  experiment_variant: GuestSpatiusAvatarVariant;
  experiment_assignment_id: string;
};

export type GuestSpatiusAvatarExposure = {
  properties: GuestSpatiusAvatarExperimentProperties;
  newlyExposed: boolean;
};

export const GUEST_SPATIUS_AVATAR_EXPOSURE_STORAGE_KEY =
  "chickytutor:experiment:guest-spatius-avatar:v1";

const EXPOSURE_TTL_MS = 8 * 24 * 60 * 60 * 1000;

type StoredExposure = GuestSpatiusAvatarExperimentProperties & {
  exposed_at_ms: number;
};

export function parseGuestSpatiusAvatarVariant(value: unknown): GuestSpatiusAvatarVariant | null {
  return value === GUEST_SPATIUS_AVATAR_VARIANTS.control ||
    value === GUEST_SPATIUS_AVATAR_VARIANTS.spatiusAvatar
    ? value
    : null;
}

function createAssignmentId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `guest_spatius_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function readStoredExposure(storage: Storage, nowMs: number): StoredExposure | null {
  try {
    const parsed = JSON.parse(
      storage.getItem(GUEST_SPATIUS_AVATAR_EXPOSURE_STORAGE_KEY) || "null"
    ) as Partial<StoredExposure> | null;
    const variant = parseGuestSpatiusAvatarVariant(parsed?.experiment_variant);
    if (
      !parsed ||
      parsed.experiment_key !== GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY ||
      !variant ||
      typeof parsed.experiment_assignment_id !== "string" ||
      !parsed.experiment_assignment_id.trim() ||
      typeof parsed.exposed_at_ms !== "number" ||
      nowMs - parsed.exposed_at_ms > EXPOSURE_TTL_MS
    ) {
      return null;
    }

    return {
      experiment_key: GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY,
      experiment_variant: variant,
      experiment_assignment_id: parsed.experiment_assignment_id,
      exposed_at_ms: parsed.exposed_at_ms,
    };
  } catch {
    return null;
  }
}

export function recordGuestSpatiusAvatarExposure({
  variant,
  storage = typeof window === "undefined" ? null : window.localStorage,
  nowMs = Date.now(),
}: {
  variant: GuestSpatiusAvatarVariant;
  storage?: Storage | null;
  nowMs?: number;
}): GuestSpatiusAvatarExposure {
  const existing = storage ? readStoredExposure(storage, nowMs) : null;
  if (existing?.experiment_variant === variant) {
    const { exposed_at_ms: _exposedAtMs, ...properties } = existing;
    return { properties, newlyExposed: false };
  }

  const properties: GuestSpatiusAvatarExperimentProperties = {
    experiment_key: GUEST_SPATIUS_AVATAR_EXPERIMENT_KEY,
    experiment_variant: variant,
    experiment_assignment_id: createAssignmentId(),
  };

  if (storage) {
    try {
      storage.setItem(
        GUEST_SPATIUS_AVATAR_EXPOSURE_STORAGE_KEY,
        JSON.stringify({ ...properties, exposed_at_ms: nowMs } satisfies StoredExposure)
      );
    } catch {
      // Attribution is best effort. The session itself must remain usable.
    }
  }

  return { properties, newlyExposed: true };
}

export function getGuestSpatiusAvatarExposureProperties({
  storage = typeof window === "undefined" ? null : window.localStorage,
  nowMs = Date.now(),
}: {
  storage?: Storage | null;
  nowMs?: number;
} = {}): GuestSpatiusAvatarExperimentProperties | Record<string, never> {
  if (!storage) return {};

  const exposure = readStoredExposure(storage, nowMs);
  if (!exposure) return {};

  const { exposed_at_ms: _exposedAtMs, ...properties } = exposure;
  return properties;
}
